import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { AssistantTurn, Presentation } from '../types';
import { PresentationContainer } from './presentation/PresentationContainer';
import { postRating } from '../api';
import { safeUrl } from '../safeUrl';

interface Props {
  turn: AssistantTurn;
  /** When true, the assistant is still streaming tokens and tools. */
  streaming: boolean;
  /** Live tool-use status line for the loading state. */
  toolsInFlight: string[];
  /** Latest backend-emitted "stage" label (e.g. "Querying HRSA data
   *  sources: hrsa_arcgis, hrsa_grants"). When present, replaces the
   *  time-based phased loader below. */
  stageLabel?: string;
}

// Phased status messages shown while the agent is "thinking" — i.e. between
// the initial /query_stream POST and the first tool_call event, OR while
// summarization tokens are pending. Real backend telemetry would be better
// (e.g. dedicated SSE "stage" events), but the orchestrator currently emits
// tool_call/tool_result only AFTER agent.run() returns, which leaves a
// 15-30s blank gap during a query. Rotating through these messages on a
// timer gives the user something useful to watch and tells them roughly
// what's happening behind the scenes.
const STATUS_PHASES: { atSeconds: number; label: string }[] = [
  { atSeconds: 0,  label: 'Analyzing your question…' },
  { atSeconds: 3,  label: 'Selecting the right HRSA tools…' },
  { atSeconds: 8,  label: 'Querying HRSA Data Warehouse…' },
  { atSeconds: 18, label: 'Cross-referencing multiple data sources…' },
  { atSeconds: 30, label: 'Synthesizing the results…' },
  { atSeconds: 45, label: 'Almost there — finishing up…' },
];

function useStatusPhase(active: boolean): string {
  const [phaseLabel, setPhaseLabel] = useState(STATUS_PHASES[0].label);
  useEffect(() => {
    if (!active) {
      setPhaseLabel(STATUS_PHASES[0].label);
      return;
    }
    const startedAt = Date.now();
    const tick = () => {
      const elapsed = (Date.now() - startedAt) / 1000;
      // Find the latest phase whose threshold has passed.
      const phase = [...STATUS_PHASES].reverse().find((p) => elapsed >= p.atSeconds);
      if (phase) setPhaseLabel(phase.label);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return phaseLabel;
}

export function AssistantMessage({ turn, streaming, toolsInFlight, stageLabel }: Props) {
  // Show the phased loader only when streaming AND nothing more useful is
  // already on screen (no in-flight tool pills, no prose tokens, no
  // backend-emitted stage label).
  const showPhasedLoader = streaming && toolsInFlight.length === 0 && !turn.prose && !stageLabel;
  const phaseLabel = useStatusPhase(showPhasedLoader);
  // Prefer the real backend stage label (e.g. "Querying HRSA data sources:
  // hrsa_arcgis, hrsa_grants") over the time-based phased fallback when
  // both are available. The backend label is shown until tool pills take
  // over OR until tokens start flowing.
  const showStageLabel = streaming && !turn.prose && Boolean(stageLabel);

  return (
    <div className="assistant-message">
      <div className="assistant-mark">HDW</div>
      <div className="assistant-body">
        {showPhasedLoader && (
          <div className="tool-use-indicator phased-loader" aria-live="polite">
            <span className="spinner" aria-hidden="true">✦</span>
            <span>{phaseLabel}</span>
          </div>
        )}
        {showStageLabel && (
          <div className="tool-use-indicator phased-loader" aria-live="polite">
            <span className="spinner" aria-hidden="true">✦</span>
            <span>{stageLabel}</span>
          </div>
        )}
        {streaming && toolsInFlight.length > 0 && (
          <div className="tool-use-indicator" aria-live="polite">
            <span className="spinner" aria-hidden="true">✦</span>
            <span>Looking up</span>
            {toolsInFlight.map((t) => (
              <span key={t} className="tool-pill" aria-hidden="true">{t}</span>
            ))}
          </div>
        )}

        {/* Composite envelopes render their own prose inside the card (map →
            table → prose, per DESIGN). Suppress the standalone prose div in
            that case to avoid rendering the narrative twice. While the answer
            is still streaming, the live region is mounted unconditionally so
            screen readers register it BEFORE the first token arrives — if we
            only mounted it once `turn.prose` was non-empty, some SRs would
            miss the announcement of subsequent tokens. */}
        {(streaming || (turn.prose && turn.presentation?.type !== 'composite')) && (
          <div
            className={`prose ${streaming ? 'prose--streaming' : ''}`}
            aria-live="polite"
            aria-busy={streaming}
          >
            {/* While streaming, render incremental tokens as plain text — partial
                markdown (e.g. an unbalanced `**`) flickers if re-parsed each
                token. Once streaming finishes, swap to react-markdown so the
                final answer's bullets/bold/italics actually format. */}
            {streaming ? (
              <>
                {turn.prose}
                <span className="prose-cursor" aria-hidden="true">▍</span>
              </>
            ) : (
              <ReactMarkdown>{turn.prose}</ReactMarkdown>
            )}
          </div>
        )}

        {!streaming && turn.presentation && needsCard(turn.presentation) && (
          <PresentationContainer p={turn.presentation} />
        )}

        {!streaming && turn.citations.length > 0 && (
          <div className="sources-footer">
            <div className="sources-footer-label">Sources</div>
            <ol>
              {turn.citations.map((c) => (
                <li key={c.index}>
                  <a href={safeUrl(c.url)} target="_blank" rel="noopener noreferrer">{c.label}</a>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Only show rating when there's a real answer to rate: not streaming,
            no error, prose came through, and we have an interaction_id from
            the backend so the click actually joins to a row. */}
        {!streaming && !turn.error && turn.prose && turn.interactionId && (
          <RatingButtons interactionId={turn.interactionId} />
        )}

        {turn.error && <div className="assistant-error">⚠ {turn.error}</div>}
      </div>
    </div>
  );
}

// Prose-only presentations don't render a card unless they have a CTA.
function needsCard(p: Presentation): boolean {
  if (p.type === 'prose') return Boolean(p.cta);
  return true;
}

function RatingButtons({ interactionId }: { interactionId: string }) {
  const [rating, setRating] = useState<'up' | 'down' | null>(null);
  return (
    <div className="rating-buttons">
      <span className="rating-caption">Was this helpful?</span>
      <button
        onClick={() => {
          setRating('up');
          postRating(interactionId, 'up').catch(() => {});
        }}
        disabled={rating !== null}
        aria-label="Helpful"
        className={rating === 'up' ? 'active' : ''}
      >
        👍
      </button>
      <button
        onClick={() => {
          setRating('down');
          postRating(interactionId, 'down').catch(() => {});
        }}
        disabled={rating !== null}
        aria-label="Not helpful"
        className={rating === 'down' ? 'active' : ''}
      >
        👎
      </button>
      {rating && <span className="rating-thanks">Thanks!</span>}
    </div>
  );
}
